import { useState, useEffect } from 'react';
import useSWR from 'swr';
import { 
  ExternalLink, 
  CreditCard, 
  DollarSign, 
  Building, 
  AlertCircle,
  RefreshCw,
  CheckCircle2,
  ArrowRight,
  Loader2,
  Shield,
  Lock,
  Zap,
  Copy,
  Check,
  Clock,
  Gift,
  Info,
  LifeBuoy
} from 'lucide-react';
import apiV2 from '../utils/apiV2';
import { toast } from 'sonner';
import { Button } from './ui/button';
import TabPageLayout, { PageLoader } from './TabPageLayout';
import { PaymentSupportCard } from './PaymentSupportCard';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';

const fmtCurrency = (amount, currency = 'USD') => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency: (currency || 'USD').toUpperCase(),
      currencyDisplay: 'narrowSymbol',
    }).format(Number(amount || 0));
  } catch {
    return `${Number(amount || 0).toFixed(2)} ${currency}`;
  }
};

const giftStatsFetcher = (url) => apiV2.get(url).then(r => r.data);

/**
 * Simplified Payments Tab for Stripe Connect Standard
 * 
 * Clean, flat design with custom shadows
 * - Shows connection status
 * - Provides quick links to Stripe Dashboard sections
 * - Handles OAuth onboarding if not connected
 */
const PaymentsTabSimple = ({ onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  // Gift card stats — small enough we can fetch on every visit
  const { data: giftStats } = useSWR(
    '/admin/gift-cards/stats/summary',
    giftStatsFetcher,
    { revalidateOnFocus: false }
  );
  const [connecting, setConnecting] = useState(false);
  const [showTrustModal, setShowTrustModal] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const orgId = localStorage.getItem('organizationId');

  const handleCopyAccountId = () => {
    if (status?.accountId) {
      navigator.clipboard.writeText(status.accountId);
      setCopied(true);
      toast.success('Account ID copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  useEffect(() => {
    // Check for OAuth callback parameters
    const urlParams = new URLSearchParams(window.location.search);
    const stripeSuccess = urlParams.get('stripe_success');
    const stripeRefresh = urlParams.get('stripe_refresh');
    const stripeError = urlParams.get('stripe_error');
    const errorMessage = urlParams.get('message');
    
    if (stripeSuccess) {
      toast.success('Stripe account connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      window.dispatchEvent(new Event('setupDataChanged'));
      // Force refresh from Stripe API after successful connection
      checkStatus(true);
    } else if (stripeRefresh) {
      // User returned to refresh/continue onboarding - sync status
      window.history.replaceState({}, '', window.location.pathname);
      checkStatus(true);
    } else if (stripeError) {
      toast.error(`Stripe connection failed: ${errorMessage || stripeError}`);
      window.history.replaceState({}, '', window.location.pathname);
      checkStatus();
    } else {
      checkStatus();
    }
  }, []);

  const checkStatus = async (forceRefresh = false) => {
    if (!orgId) {
      setLoading(false);
      return;
    }
    try {
      const response = await apiV2.get(`/organizations/${orgId}/stripe-connect/status`, {
        params: { refresh: forceRefresh }
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Error checking Stripe status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = () => {
    setShowTrustModal(true);
  };

  const handleConfirmStripe = async () => {
    setShowTrustModal(false);
    setConnecting(true);
    
    try {
      const response = await apiV2.get(`/organizations/${orgId}/stripe-connect/oauth-url`);
      const { url } = response.data;
      
      if (!url) {
        throw new Error('Failed to generate Stripe connection URL');
      }
      
      window.location.href = url;
    } catch (err) {
      console.error('Error starting Stripe connection:', err);
      toast.error(err.response?.data?.detail || 'Failed to connect to Stripe');
      setConnecting(false);
    }
  };

  // Quick link cards data
  const quickLinks = [
    {
      title: 'Payments',
      description: 'View all transactions',
      icon: DollarSign,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      url: 'https://dashboard.stripe.com/payments'
    },
    {
      title: 'Payouts',
      description: 'Bank transfers',
      icon: Building,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
      url: 'https://dashboard.stripe.com/payouts'
    },
    {
      title: 'Disputes',
      description: 'Handle chargebacks',
      icon: AlertCircle,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600',
      url: 'https://dashboard.stripe.com/disputes'
    },
    {
      title: 'Refunds',
      description: 'Issue refunds',
      icon: RefreshCw,
      iconBg: 'bg-violet-50',
      iconColor: 'text-violet-600',
      url: 'https://dashboard.stripe.com/refunds'
    }
  ];

  if (loading) {
    return (
      <TabPageLayout title="Overview" subtitle="Manage your payment processing">
        <PageLoader />
      </TabPageLayout>
    );
  }

  // Determine the actual state
  const isPendingVerification = status?.connected && status?.detailsSubmitted && !status?.chargesEnabled;
  const hasRequirements = status?.requirements?.currentlyDue?.length > 0 || status?.requirements?.pastDue?.length > 0;

  // PENDING VERIFICATION STATE - User submitted details but Stripe is still verifying
  if (isPendingVerification) {
    return (
      <TabPageLayout 
        title="Stripe" 
        subtitle="Your Stripe account is being verified"
      >
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden">
            {/* Header - Amber/Yellow for pending */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-8 py-10 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur rounded-2xl mb-5">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {hasRequirements ? 'Action Required' : 'Verification in Progress'}
              </h2>
              <p className="text-white/80 max-w-md mx-auto">
                {hasRequirements 
                  ? 'Stripe needs additional information to complete your account setup'
                  : 'Stripe is reviewing your information. This typically takes a few minutes, but can take up to 24 hours.'
                }
              </p>
            </div>
            
            {/* Content */}
            <div className="p-8">
              {/* Status indicators */}
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Account Created</p>
                    <p className="text-sm text-gray-500">Your Stripe account has been set up</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Details Submitted</p>
                    <p className="text-sm text-gray-500">Your business information has been received</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl ${hasRequirements ? 'bg-amber-50' : 'bg-blue-50'} flex items-center justify-center flex-shrink-0`}>
                    {hasRequirements ? (
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                    ) : (
                      <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {hasRequirements ? 'Additional Info Needed' : 'Verification Pending'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {hasRequirements 
                        ? 'Please complete the remaining steps on Stripe'
                        : 'Stripe is reviewing your account (usually 5-30 minutes)'
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Info box */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">
                      {hasRequirements ? 'Complete your verification on Stripe' : 'What happens next?'}
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      {hasRequirements 
                        ? 'Stripe may need additional documents like a photo ID or bank details. Click the button below to complete the process.'
                        : 'Once Stripe verifies your information, you\'ll be able to accept payments immediately. Check back in a few minutes or refresh below.'
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <Button
                  onClick={() => window.open('https://dashboard.stripe.com/settings/account', '_blank')}
                  className="w-full bg-[#635BFF] hover:bg-[#5851DB] text-white h-12 text-base font-medium rounded-xl"
                >
                  <ExternalLink className="w-5 h-5 mr-2" />
                  {hasRequirements ? 'Complete Setup on Stripe' : 'Check Status on Stripe'}
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => {
                    setLoading(true);
                    checkStatus(true);
                  }}
                  disabled={loading}
                  className="w-full h-10 text-gray-600 hover:text-gray-900 rounded-xl"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Refresh Status
                </Button>
              </div>
              
              {/* Account ID if available */}
              {status?.accountId && (
                <p className="text-center text-xs text-gray-400 mt-4">
                  Account ID: {status.accountId}
                </p>
              )}
            </div>
          </div>
        </div>
      </TabPageLayout>
    );
  }

  // Not connected - show onboarding
  if (!status?.onboardingComplete) {
    return (
      <TabPageLayout 
        title="Stripe" 
        subtitle="Connect Stripe to start accepting payments"
      >
        <div className="max-w-2xl mx-auto">
          {/* Onboarding Card - Flat design with shadow */}
          <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#635BFF] to-[#7C3AED] px-8 py-10 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur rounded-2xl mb-5">
                <CreditCard className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                Accept Payments Online
              </h2>
              <p className="text-white/80 max-w-md mx-auto">
                Connect your Stripe account to start receiving payments from customers
              </p>
            </div>
            
            {/* Content */}
            <div className="p-8">
              {/* Benefits */}
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Quick Setup</p>
                    <p className="text-sm text-gray-500">Connect in under 3 minutes</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Lock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Secure & Direct</p>
                    <p className="text-sm text-gray-500">Payments go straight to your account</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Full Control</p>
                    <p className="text-sm text-gray-500">Manage everything in your Stripe Dashboard</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <Button
                  onClick={handleConnect}
                  disabled={connecting}
                  className="w-full bg-[#635BFF] hover:bg-[#5851DB] text-white h-12 text-base font-medium rounded-xl"
                >
                  {connecting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                      </svg>
                      Connect with Stripe
                    </>
                  )}
                </Button>
                
                <Button
                  variant="ghost"
                  onClick={() => checkStatus(true)}
                  className="w-full h-10 text-gray-600 hover:text-gray-900"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Check Connection Status
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Modal — uses our Dialog (bottom sheet on mobile) */}
        <Dialog open={showTrustModal} onOpenChange={setShowTrustModal}>
          <DialogContent className="sm:max-w-md">
            <DialogTitle className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#635BFF]/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-[#635BFF]" />
              </div>
              Connect to Stripe
            </DialogTitle>
            <p className="text-sm text-gray-600">You'll be redirected to Stripe to complete the connection.</p>
            <div className="space-y-2 bg-gray-50 rounded-xl p-4 mt-1">
              {['Payments deposited directly to you', 'Full access to your Stripe Dashboard', 'Cancel anytime'].map(item => (
                <div key={item} className="flex items-center gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span className="text-sm text-gray-700">{item}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowTrustModal(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleConfirmStripe} disabled={connecting} className="flex-1 bg-[#635BFF] hover:bg-[#5851DB] text-white">
                {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4 ml-1.5" /></>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </TabPageLayout>
    );
  }

  // Connected - show dashboard
  return (
    <TabPageLayout 
      title="Stripe" 
      subtitle="Manage your payment processing"
    >
      <div className="space-y-4">
        {/* Status Badge */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full">
            <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium text-emerald-700">Stripe Connected</span>
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          </div>
          {status?.accountId && (
            <button
              onClick={handleCopyAccountId}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              <span>•••{status.accountId.slice(-6)}</span>
              {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
            </button>
          )}
        </div>

        {/* Need help with payments? — full Stripe account ID + support/dashboard links */}
        <PaymentSupportCard />

        {/* Quick Links Grid — 2×2 on mobile, 4 on desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {quickLinks.map((link) => (
            <button
              key={link.title}
              onClick={() => window.open(link.url, '_blank')}
              className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-4 text-left hover:shadow-[0_12px_32px_rgba(0,0,0,0.08)] transition-shadow group flex flex-col"
            >
              <div className="flex items-center justify-between mb-2">
                <div className={`w-8 h-8 rounded-lg ${link.iconBg} flex items-center justify-center`}>
                  <link.icon className={`w-4 h-4 ${link.iconColor}`} />
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900">{link.title}</h3>
              <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{link.description}</p>
            </button>
          ))}
        </div>

        {/* Refund nudge banner — encourages gift card issuance before processing refunds */}
        <div
          className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl p-4 flex items-start gap-3"
          data-testid="refund-giftcard-nudge"
        >
          <div className="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0">
            <Gift className="w-4 h-4 text-indigo-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-gray-900 mb-0.5">Before issuing a refund</h4>
            <p className="text-xs text-gray-600 leading-relaxed">
              Consider offering a gift card instead — it preserves your earned revenue and keeps the customer in your system for a future booking.
              <button
                onClick={() => onNavigate?.('giftCards')}
                className="text-indigo-600 hover:text-indigo-700 font-medium ml-1 underline-offset-2 hover:underline"
                data-testid="refund-banner-issue-giftcard-btn"
              >
                Issue a gift card →
              </button>
            </p>
          </div>
        </div>

        {/* Crypto Card — white, no dark gradient */}
        <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-5">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
              <span className="text-indigo-600 font-bold text-base">₿</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h4 className="text-sm font-semibold text-gray-900">Accept Crypto Payments</h4>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">US Operators</span>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Customers pay with USDC — you receive USD. Stripe handles conversion, no volatility risk.
              </p>
              <ol className="text-xs text-gray-400 space-y-1">
                <li>1. Stripe Dashboard → <button onClick={() => window.open('https://dashboard.stripe.com/settings/payment_methods', '_blank')} className="text-blue-600 hover:underline">Settings → Payment Methods</button></li>
                <li>2. Search "Crypto" → Turn On</li>
                <li>3. Crypto appears at checkout automatically after approval</li>
              </ol>
            </div>
            <Button
              onClick={() => window.open('https://dashboard.stripe.com/settings/payment_methods', '_blank')}
              variant="outline"
              className="flex-shrink-0 h-8 px-3 text-xs font-medium hidden sm:flex"
            >
              Enable →
            </Button>
          </div>
        </div>
      </div>
    </TabPageLayout>
  );
};

export default PaymentsTabSimple;
